import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";

import workspaceSkillsQuery from "@apps/org-settings/graphql/workspace-skills-query";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import Placeholder from "@components/placeholder/placeholder";

import createOrUpdateSkillMutation from "../../graphql/create-or-update-skill-mutation";
import forkPlatformSkillMutation from "../../graphql/fork-platform-skill-mutation";
import mySkillsQuery from "../../graphql/my-skills-query";
import platformSkillsQuery from "../../graphql/platform-skills-query";
import SkillEditModal from "./skill-edit-modal";

type SkillRow = {
  id: string;
  name: string;
  slug: string;
  description: string;
  body: string;
};

type ForkedSkill = {
  id: string;
  name: string;
  description: string;
  body: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** When provided, the modal also lists this org's workspace skills. */
  organizationId: number | null;
};

const SectionHeader = ({ label }: { label: string }) => (
  <tr>
    <td
      colSpan={2}
      className="bg-gray-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b"
    >
      {label}
    </td>
  </tr>
);

const BrowseSkillsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  organizationId,
}) => {
  const workspace = useQuery<any>(workspaceSkillsQuery, {
    fetchPolicy: "cache-and-network",
    skip: !isOpen || !organizationId,
    variables: { organizationId: organizationId ?? 0 },
  });
  const platform = useQuery<any>(platformSkillsQuery, {
    fetchPolicy: "cache-and-network",
    skip: !isOpen,
    variables: { organizationId: null },
  });

  const [createOrUpdate, { loading: forkingWorkspace }] = useMutation<any>(
    createOrUpdateSkillMutation,
    { refetchQueries: [mySkillsQuery] },
  );
  const [forkPlatform, { loading: forkingPlatform }] = useMutation<any>(
    forkPlatformSkillMutation,
    { refetchQueries: [mySkillsQuery] },
  );
  const [forkedSkill, setForkedSkill] = useState<ForkedSkill | null>(null);
  const forking = forkingWorkspace || forkingPlatform;

  const workspaceRows: SkillRow[] = workspace.data?.workspaceSkills ?? [];
  const platformRows: SkillRow[] = platform.data?.platformSkills ?? [];
  const loading =
    (workspace.loading || platform.loading) &&
    workspaceRows.length === 0 &&
    platformRows.length === 0;

  const finishFork = (created: ForkedSkill | undefined, sourceName: string) => {
    if (!created) return;
    successNotificationVar({ title: `Copied "${sourceName}" to your skills` });
    setForkedSkill({
      id: created.id,
      name: created.name,
      description: created.description,
      body: created.body,
    });
    onClose();
  };

  const handleForkWorkspace = async (skill: SkillRow) => {
    try {
      const result = await createOrUpdate({
        variables: {
          name: skill.name,
          description: skill.description,
          body: skill.body,
        },
      });
      finishFork(result.data?.createOrUpdateSkill?.skill, skill.name);
    } catch (e) {
      errorNotificationVar({
        title: "Could not copy skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleForkPlatform = async (skill: SkillRow) => {
    try {
      const result = await forkPlatform({
        variables: { slug: skill.slug, organizationId: null },
      });
      finishFork(result.data?.forkPlatformSkill?.skill, skill.name);
    } catch (e) {
      errorNotificationVar({
        title: "Could not copy skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const renderRow = (skill: SkillRow, onFork: () => void) => (
    <tr key={skill.id} aria-label={`Skill row ${skill.name}`}>
      <td className="px-4 py-2 align-top">
        <div className="font-mono text-blue-link">/{skill.slug}</div>
        {skill.description && (
          <div className="text-xs text-gray-400 mt-0.5">
            {skill.description}
          </div>
        )}
      </td>
      <td className="px-4 py-2 align-top whitespace-nowrap">
        <Button text="Make a copy" disabled={forking} onClick={onFork} />
      </td>
    </tr>
  );

  const hasAnyRow = workspaceRows.length > 0 || platformRows.length > 0;

  return (
    <>
      <SkillEditModal
        isOpen={!!forkedSkill}
        onClose={() => setForkedSkill(null)}
        initial={forkedSkill ?? undefined}
      />
      {isOpen && (
        <Modal
          open
          onClose={onClose}
          aria-label="Browse skills dialog"
          dialogClassName="md:max-w-3xl"
        >
          <div className="p-6 bg-white rounded-md">
            <div className="mb-4">
              <ModalTitle onClose={onClose}>Browse skills</ModalTitle>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Skills shared by your workspace or shipped by Topicflow. Make a
              copy to customize it as your own personal skill you can edit.
            </p>

            {loading ? (
              <div className="flex justify-center my-8">
                <Loading mini size="5" />
              </div>
            ) : !hasAnyRow ? (
              <Placeholder>No skills available to copy.</Placeholder>
            ) : (
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <tbody className="divide-y">
                    {workspaceRows.length > 0 && (
                      <>
                        <SectionHeader label="Workspace skills" />
                        {workspaceRows.map((skill) =>
                          renderRow(skill, () => handleForkWorkspace(skill)),
                        )}
                      </>
                    )}
                    {platformRows.length > 0 && (
                      <>
                        <SectionHeader label="Platform skills" />
                        {platformRows.map((skill) =>
                          renderRow(skill, () => handleForkPlatform(skill)),
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default BrowseSkillsModal;
